import { useState, useEffect, useCallback } from 'react';
import { exportCsv, dateStamp } from '../services/exportCsv';

/**
 * OperatorsPage — add robot operators without manual MySQL inserts.
 *
 * Talks to the evesix-operators API (Cloudflare Worker → Hyperdrive → MySQL
 * `operator` table in robotdb). Robots validate PINs live against that table,
 * so a newly created operator can log in on the robots immediately.
 */

const API_BASE = import.meta.env.VITE_OPERATORS_API_URL || 'https://evesix-operators-dev.reshigan-085.workers.dev';

const SHIFTS = ['Day', 'Night', 'Day/Night', 'All day'];
const COMMON_ROLES = ['Operator', 'Zapper', 'Cleaner', 'Team leader', 'Supervisor', 'Housekeeping', 'Porter', 'Nurse', 'IPC', 'Admin'];

// The operators worker verifies this portal login token against the auth
// worker and requires the Super Admin role.
async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('portal_auth_token') || ''}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) {
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return body;
}

export default function OperatorsPage({ user }) {
  const [operators, setOperators] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [created, setCreated] = useState(null); // last created operator (shows PIN once)

  // Load the full operator list once; searching filters in-memory (instant).
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api('/operators');
      setOperators(res.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const q = query.trim().toLowerCase();
  const visible = q
    ? operators.filter((op) =>
        [op.First_name, op.Last_name, op.Username, op.Role, String(op.Operator_Id), String(op.CODE ?? '')]
          .some((v) => (v || '').toLowerCase().includes(q))
      )
    : operators;
  const total = operators.length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Robot Operators</h1>
          <p className="text-sm text-slate-500 mt-1">
            {q ? `${visible.length.toLocaleString()} of ${total.toLocaleString()} operators` : `${total.toLocaleString()} operators`} · new operators can log in on robots immediately
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() =>
              exportCsv(`evesix-operators-${dateStamp()}.csv`, [
                { key: 'Operator_Id', label: 'ID' },
                { key: 'First_name', label: 'First name' },
                { key: 'Last_name', label: 'Last name' },
                { key: 'Username', label: 'Username' },
                { key: 'CODE', label: 'PIN' },
                { key: 'Role', label: 'Role' },
                { key: 'Shift', label: 'Shift' },
              ], visible)
            }
            disabled={loading || visible.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-semibold rounded-xl border border-slate-200 shadow-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" />
            </svg>
            Export CSV{q ? ` (${visible.length})` : ''}
          </button>
          <button
            onClick={() => { setCreated(null); setShowForm(true); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Operator
          </button>
        </div>
      </div>

      {/* PIN handover banner (shown once after create) */}
      {created && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-emerald-800 font-semibold">
              ✅ {created.First_name} {created.Last_name} created (ID {created.Operator_Id})
            </p>
            <p className="text-emerald-700 text-sm mt-1">
              Robot login PIN: <span className="font-mono font-bold text-lg tracking-widest">{created.CODE}</span>
              {' '}— hand this to the operator now; it is not shown again in the portal.
            </p>
          </div>
          <button onClick={() => setCreated(null)} className="text-emerald-500 hover:text-emerald-700 text-xl leading-none">×</button>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, username or role…"
          className="w-full sm:w-96 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 caret-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-4">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-5 py-3">ID</th>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Username</th>
              <th className="px-5 py-3">PIN</th>
              <th className="px-5 py-3">Role</th>
              <th className="px-5 py-3">Shift</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Loading operators…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No operators match your search.</td></tr>
            ) : visible.map((op) => (
              <tr key={op.Operator_Id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                <td className="px-5 py-3 font-mono text-slate-500">{op.Operator_Id}</td>
                <td className="px-5 py-3 font-medium text-slate-800">{op.First_name} {op.Last_name}</td>
                <td className="px-5 py-3 text-slate-600">{op.Username || '—'}</td>
                <td className="px-5 py-3 font-mono font-semibold text-slate-800 tracking-wider">{op.CODE ?? '—'}</td>
                <td className="px-5 py-3"><span className="inline-block px-2.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs">{(op.Role || '—').trim() || '—'}</span></td>
                <td className="px-5 py-3 text-slate-600">{op.Shift || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <AddOperatorModal
          onClose={() => setShowForm(false)}
          onCreated={(op) => { setShowForm(false); setCreated(op); load(); }}
        />
      )}
    </div>
  );
}

function AddOperatorModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', username: '', role: 'Operator', shift: 'Day', code: '' });
  const [autoPin, setAutoPin] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        username: form.username || undefined,
        role: form.role,
        shift: form.shift,
        ...(autoPin ? {} : { code: form.code }),
      };
      const res = await api('/operators', { method: 'POST', body: JSON.stringify(payload) });
      onCreated(res.data);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 backdrop-blur-sm p-4 pt-20" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Add Operator</h2>
            <p className="text-xs text-slate-500 mt-0.5">Creates the operator in the robot database — no manual SQL needed</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">×</button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="First name *">
              <input required value={form.first_name} onChange={set('first_name')} className={inputCls} placeholder="e.g. Thandi" />
            </Field>
            <Field label="Last name *">
              <input required value={form.last_name} onChange={set('last_name')} className={inputCls} placeholder="e.g. Mokoena" />
            </Field>
          </div>

          <Field label="Username (defaults to full name)">
            <input value={form.username} onChange={set('username')} className={inputCls} placeholder="optional display name" />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Role">
              <select value={form.role} onChange={set('role')} className={inputCls}>
                {COMMON_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Shift">
              <select value={form.shift} onChange={set('shift')} className={inputCls}>
                {SHIFTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 space-y-3">
            <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={autoPin} onChange={(e) => setAutoPin(e.target.checked)} className="rounded border-slate-300" />
              Auto-assign a free 4-digit robot login PIN
            </label>
            {!autoPin && (
              <Field label="PIN (4-6 digits, must be unique)">
                <input value={form.code} onChange={set('code')} className={inputCls} placeholder="e.g. 4821" pattern="\d{4,6}" />
              </Field>
            )}
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{error}</div>}

          <div className="flex justify-end gap-3 pt-2 pb-1">
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-800">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl shadow-sm"
            >
              {saving ? 'Creating…' : 'Create operator'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 caret-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
